import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import DashboardLayout from './_components/DashboardLayout';
import { TrendingUp, TrendingDown, FileText, CheckCircle, Clock, XCircle, Eye, CheckSquare, X, IndianRupee, RefreshCw } from 'lucide-react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalLoans: 0,
    approvedLoans: 0,
    pendingLoans: 0,
    rejectedLoans: 0,
    totalDisbursed: 0,
    recentApplications: [],
    monthlyTrends: [],
    loanTypeDistribution: {
      personal: 0,
      business: 0,
      vehicle: 0,
      home: 0,
      education: 0
    },
    previousMonthStats: {
      total: 0,
      approved: 0,
      pending: 0,
      rejected: 0
    }
  });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((u) => {
      if (u) {
        setUser(u);
      } else {
        navigate('/login');
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
    }
  }, [user]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch Personal Loans
      const personalLoansRef = collection(db, 'personalLoan');
      const personalLoansSnapshot = await getDocs(personalLoansRef);
      const personalLoans = personalLoansSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'Personal' }));
      
      // Fetch Business Loans
      const businessLoansRef = collection(db, 'businessLoans');
      const businessLoansSnapshot = await getDocs(businessLoansRef);
      const businessLoans = businessLoansSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'Business' }));
      
      // Combine all loans
      const allLoans = [...personalLoans, ...businessLoans];
      
      // Calculate statistics
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
      const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
      
      // Current month stats
      const totalLoans = allLoans.length;
      const approvedLoans = allLoans.filter(l => l.status === 'approved' || l.status === 'disbursed').length;
      const pendingLoans = allLoans.filter(l => l.status === 'pending' || l.status === 'under-review').length;
      const rejectedLoans = allLoans.filter(l => l.status === 'rejected').length;
      
      // Calculate total disbursed amount
      const totalDisbursed = allLoans
        .filter(l => l.status === 'approved' || l.status === 'disbursed')
        .reduce((sum, l) => sum + (Number(l.loanAmount) || 0), 0);
      
      // Previous month stats for comparison
      const lastMonthLoans = allLoans.filter(l => {
        if (!l.createdAt) return false;
        const date = l.createdAt.toDate ? l.createdAt.toDate() : new Date(l.createdAt);
        return date.getMonth() === lastMonth && date.getFullYear() === lastMonthYear;
      });
      
      const previousMonthStats = {
        total: lastMonthLoans.length,
        approved: lastMonthLoans.filter(l => l.status === 'approved' || l.status === 'disbursed').length,
        pending: lastMonthLoans.filter(l => l.status === 'pending' || l.status === 'under-review').length,
        rejected: lastMonthLoans.filter(l => l.status === 'rejected').length
      };
      
      // Loan type distribution
      const loanTypeDistribution = {
        personal: personalLoans.length,
        business: businessLoans.length,
        vehicle: 0, // Not implemented yet
        home: 0, // Not implemented yet
        education: 0 // Not implemented yet
      };
      
      // Monthly trends (last 10 months)
      const monthlyTrends = [];
      for (let i = 9; i >= 0; i--) {
        const targetMonth = currentMonth - i;
        const targetYear = currentYear + Math.floor(targetMonth / 12);
        const normalizedMonth = ((targetMonth % 12) + 12) % 12;
        
        const monthLoans = allLoans.filter(l => {
          if (!l.createdAt) return false;
          const date = l.createdAt.toDate ? l.createdAt.toDate() : new Date(l.createdAt);
          return date.getMonth() === normalizedMonth && date.getFullYear() === targetYear;
        });
        
        monthlyTrends.push({
          month: new Date(targetYear, normalizedMonth).toLocaleDateString('en-US', { month: 'short' }),
          count: monthLoans.length
        });
      }
      
      // Recent applications (last 10)
      const sortedLoans = allLoans.sort((a, b) => {
        // Use applicationDate for business loans, createdAt for personal loans
        const dateFieldA = a.type === 'Business' ? a.applicationDate : a.createdAt;
        const dateFieldB = b.type === 'Business' ? b.applicationDate : b.createdAt;
        
        const dateA = dateFieldA?.toDate ? dateFieldA.toDate() : new Date(dateFieldA);
        const dateB = dateFieldB?.toDate ? dateFieldB.toDate() : new Date(dateFieldB);
        return dateB - dateA;
      });
      
      const recentApplications = sortedLoans.slice(0, 10).map(loan => {
        const isPersonal = loan.type === 'Personal';
        const dateField = isPersonal ? loan.createdAt : loan.applicationDate;
        return {
          id: loan.id,
          applicant: isPersonal ? loan.fullName : (loan.fullName || loan.businessName),
          loanType: loan.type,
          amount: loan.loanAmount,
          status: loan.status,
          date: dateField,
          route: isPersonal ? `/personal-loans/${loan.id}` : `/business-loans/${loan.id}`
        };
      });
      
      setDashboardData({
        totalLoans,
        approvedLoans,
        pendingLoans,
        rejectedLoans,
        totalDisbursed,
        recentApplications,
        monthlyTrends,
        loanTypeDistribution,
        previousMonthStats
      });
      
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const calculateTrend = (current, previous) => {
    // If both are 0, show no change
    if (current === 0 && previous === 0) {
      return { value: '0%', isPositive: true };
    }
    
    // If previous is 0 but current has value, show new entries
    if (previous === 0 && current > 0) {
      return { value: `+${current} new`, isPositive: true };
    }
    
    // If current is 0 but previous had value
    if (current === 0 && previous > 0) {
      return { value: '-100%', isPositive: false };
    }
    
    // Normal percentage calculation
    const percentage = ((current - previous) / previous) * 100;
    return {
      value: `${percentage >= 0 ? '+' : ''}${percentage.toFixed(1)}%`,
      isPositive: percentage >= 0
    };
  };

  const getTrendInfo = (current, previous, lowerIsBetter = false) => {
    const trendData = calculateTrend(current, previous);
    
    // For metrics where lower is better (pending, rejected)
    if (lowerIsBetter) {
      // If both are zero or same, neutral
      if (current === previous) {
        return { ...trendData, isPositive: true };
      }
      // Lower is good (green), higher is bad (red)
      return { ...trendData, isPositive: current < previous };
    }
    
    // For normal metrics (higher is better)
    return trendData;
  };

  // Dynamic data for analytics cards
  const stats = [
    { 
      label: 'Total Loan Applications', 
      value: dashboardData.totalLoans.toLocaleString('en-IN'), 
      trend: getTrendInfo(dashboardData.totalLoans, dashboardData.previousMonthStats.total).value,
      isPositive: getTrendInfo(dashboardData.totalLoans, dashboardData.previousMonthStats.total).isPositive,
      icon: FileText,
      color: 'blue' 
    },
    { 
      label: 'Approved Loans', 
      value: dashboardData.approvedLoans.toLocaleString('en-IN'), 
      trend: getTrendInfo(dashboardData.approvedLoans, dashboardData.previousMonthStats.approved).value,
      isPositive: getTrendInfo(dashboardData.approvedLoans, dashboardData.previousMonthStats.approved).isPositive,
      icon: CheckCircle,
      color: 'green' 
    },
    { 
      label: 'Pending Loans', 
      value: dashboardData.pendingLoans.toLocaleString('en-IN'), 
      trend: getTrendInfo(dashboardData.pendingLoans, dashboardData.previousMonthStats.pending, true).value,
      isPositive: getTrendInfo(dashboardData.pendingLoans, dashboardData.previousMonthStats.pending, true).isPositive,
      icon: Clock,
      color: 'yellow' 
    },
    { 
      label: 'Rejected Loans', 
      value: dashboardData.rejectedLoans.toLocaleString('en-IN'), 
      trend: getTrendInfo(dashboardData.rejectedLoans, dashboardData.previousMonthStats.rejected, true).value,
      isPositive: getTrendInfo(dashboardData.rejectedLoans, dashboardData.previousMonthStats.rejected, true).isPositive,
      icon: XCircle,
      color: 'red' 
    },
  ];

  // Bar chart data - Monthly loan trends (dynamic)
  const barChartData = {
    labels: dashboardData.monthlyTrends.map(m => m.month),
    datasets: [
      {
        label: 'Loan Applications',
        data: dashboardData.monthlyTrends.map(m => m.count),
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 1,
      },
    ],
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  // Donut chart data - Loan types distribution (dynamic)
  const doughnutChartData = {
    labels: ['Personal', 'Business', 'Vehicle', 'Home', 'Education'],
    datasets: [
      {
        data: [
          dashboardData.loanTypeDistribution.personal,
          dashboardData.loanTypeDistribution.business,
          dashboardData.loanTypeDistribution.vehicle,
          dashboardData.loanTypeDistribution.home,
          dashboardData.loanTypeDistribution.education
        ],
        backgroundColor: [
          'rgba(59, 130, 246, 0.8)',
          'rgba(16, 185, 129, 0.8)',
          'rgba(245, 158, 11, 0.8)',
          'rgba(239, 68, 68, 0.8)',
          'rgba(139, 92, 246, 0.8)',
        ],
        borderColor: [
          'rgba(59, 130, 246, 1)',
          'rgba(16, 185, 129, 1)',
          'rgba(245, 158, 11, 1)',
          'rgba(239, 68, 68, 1)',
          'rgba(139, 92, 246, 1)',
        ],
        borderWidth: 2,
      },
    ],
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          padding: 15,
          font: {
            size: 12,
          },
        },
      },
      title: {
        display: false,
      },
    },
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    try {
      let date;
      if (timestamp?.toDate) {
        date = timestamp.toDate();
      } else if (timestamp?.seconds) {
        date = new Date(timestamp.seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      return date.toLocaleDateString('en-IN', { 
        day: 'numeric',
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatLargeAmount = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) { // 1 crore or more
      return `₹${(amount / 10000000).toFixed(2)} Crore`;
    } else if (amount >= 100000) { // 1 lakh or more
      return `₹${(amount / 100000).toFixed(2)} Lakh`;
    }
    return formatCurrency(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800',
      'under-review': 'bg-blue-100 text-blue-800',
      disbursed: 'bg-purple-100 text-purple-800',
      completed: 'bg-gray-100 text-gray-800',
    };
    return styles[status?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status) => {
    if (!status) return 'Pending';
    return status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ');
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-100 text-blue-600',
      green: 'bg-green-100 text-green-600',
      yellow: 'bg-yellow-100 text-yellow-600',
      red: 'bg-red-100 text-red-600',
    };
    return colors[color] || 'bg-gray-100 text-gray-600';
  };

  if (!user) return null;

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Loading dashboard data...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Dashboard Overview</h1>
            <p className="text-gray-600 text-sm mt-1">Monitor your loan applications and business metrics</p>
          </div>
          <button
            onClick={fetchDashboardData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium shadow-sm"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div 
              key={index} 
              className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 hover:shadow-md hover:border-gray-200 transition-all duration-300 group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-gray-600 text-xs sm:text-sm font-medium mb-2">{stat.label}</p>
                  <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">{stat.value}</h3>
                  <div className="flex items-center gap-1.5">
                    {stat.isPositive ? (
                      <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-600" />
                    ) : (
                      <TrendingDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-600" />
                    )}
                    <span className={`text-xs sm:text-sm font-semibold ${stat.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                      {stat.trend}
                    </span>
                    <span className="text-gray-500 text-xs hidden sm:inline">vs last month</span>
                  </div>
                </div>
                <div className={`p-3 sm:p-3.5 rounded-xl ${getColorClasses(stat.color)} group-hover:scale-110 transition-transform duration-300`}>
                  <Icon className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total Disbursed Card */}
      <div className="bg-linear-to-br from-blue-600 via-blue-700 to-purple-700 rounded-2xl shadow-lg p-6 sm:p-8 mb-6 sm:mb-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <IndianRupee className="w-5 h-5 text-blue-100" />
              <p className="text-blue-100 text-sm font-medium">Total Disbursed Amount</p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">{formatLargeAmount(dashboardData.totalDisbursed)}</h2>
            <p className="text-blue-100 text-sm">Across all approved loans ({dashboardData.approvedLoans} loans)</p>
          </div>
          <div className="hidden sm:flex bg-white/10 backdrop-blur-sm p-4 sm:p-5 rounded-2xl border border-white/20">
            <FileText className="w-8 h-8 sm:w-10 sm:h-10" />
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
        {/* Bar Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-shadow">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Monthly Loan Trends</h3>
          <div className="h-64 sm:h-80">
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        </div>

        {/* Donut Chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-md transition-shadow">
          <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-4">Loan Type Distribution</h3>
          <div className="h-64 sm:h-80">
            <Doughnut data={doughnutChartData} options={doughnutChartOptions} />
          </div>
        </div>
      </div>

      {/* Recent Applications Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">Recent Loan Applications</h3>
          <p className="text-sm text-gray-500 mt-1">Latest applications requiring attention</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Applicant
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Loan Type
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider hidden sm:table-cell">
                  Date
                </th>
                <th className="px-4 sm:px-6 py-3 sm:py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {dashboardData.recentApplications.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 sm:px-6 py-12 text-center">
                    <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">No recent applications found</p>
                  </td>
                </tr>
              ) : (
                dashboardData.recentApplications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="font-semibold text-sm text-gray-900">{app.applicant || 'N/A'}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{app.loanType}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="font-bold text-sm text-gray-900">{formatCurrency(app.amount)}</div>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(app.status)}`}>
                        {getStatusLabel(app.status)}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-500 hidden sm:table-cell">
                      {formatDate(app.date)}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-1 sm:gap-2">
                        <button 
                          onClick={() => navigate(app.route)}
                          className="p-1.5 sm:p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" 
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
